import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { toast } from 'sonner';
import { Wrench, CreditCard, Loader2, Clock, Megaphone, Truck, UserX, ShoppingCart } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Admin card for site-wide toggles:
 *  • Maintenance mode — flips the storefront to "We'll be back soon".
 *  • Online payments — hides the PayPal checkout & blocks PayPal endpoints.
 *
 * Both update the single `site_settings` doc on the backend. The customer
 * site re-polls /api/site-settings every 60s so toggles take effect within
 * a minute without a forced reload.
 */
const SiteSettingsCard = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [annMsg, setAnnMsg] = useState('');
  const [annCode, setAnnCode] = useState('');
  const [annStart, setAnnStart] = useState('');
  const [annEnd, setAnnEnd] = useState('');
  const [freeShip, setFreeShip] = useState('');
  const [cutoffEnabled, setCutoffEnabled] = useState(true);
  const [cutoffTime, setCutoffTime] = useState('');
  const [reEnabled, setReEnabled] = useState(true);
  const [reWarn, setReWarn] = useState(90);
  const [reGrace, setReGrace] = useState(30);
  const [reRestock, setReRestock] = useState(60);
  const [reDigest, setReDigest] = useState(true);
  const [incentiveCode, setIncentiveCode] = useState('');
  const [incentivePercent, setIncentivePercent] = useState(20);
  const [cartEnabled, setCartEnabled] = useState(true);
  const [cartFirstHours, setCartFirstHours] = useState(4);
  const [cartReminderHours, setCartReminderHours] = useState(24);

  useEffect(() => {
    if (settings) {
      setAnnMsg(settings.announcement_message || '');
      setAnnCode(settings.announcement_code || '');
      setAnnStart(settings.announcement_start || '');
      setAnnEnd(settings.announcement_end || '');
      setFreeShip(settings.free_shipping_threshold ?? 100);
      setCutoffEnabled(settings.shipping_cutoff_enabled ?? true);
      setCutoffTime(settings.shipping_cutoff_time ?? '3:00 PM PT');
      setReEnabled(settings.reengagement_enabled ?? true);
      setReWarn(settings.reengagement_warning_days ?? 90);
      setReGrace(settings.reengagement_grace_days ?? 30);
      setReRestock(settings.reengagement_restock_days ?? 60);
      setReDigest(settings.reengagement_digest_enabled ?? true);
      setIncentiveCode(settings.incentive_discount_code ?? '');
      setIncentivePercent(settings.incentive_discount_percent ?? 20);
      setCartEnabled(settings.abandoned_cart_enabled ?? true);
      setCartFirstHours(settings.abandoned_cart_first_hours ?? 4);
      setCartReminderHours(settings.abandoned_cart_reminder_hours ?? 24);
    }
  }, [settings]);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/site-settings`, { cache: 'no-store' });
      if (r.ok) setSettings(await r.json());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const update = async (patch) => {
    setSaving(true);
    try {
      const token = localStorage.getItem('adminToken');
      const r = await fetch(`${API_URL}/api/admin/site-settings`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(patch),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${r.status}`);
      }
      const next = await r.json();
      setSettings(next);
      toast.success('Site settings updated');
    } catch (e) {
      toast.error(`Update failed: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6 flex items-center justify-center text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading site settings…
        </CardContent>
      </Card>
    );
  }

  if (!settings) return null;

  return (
    <Card data-testid="site-settings-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Wrench className="h-5 w-5 text-teal-600" />
          Site Status
        </CardTitle>
        <p className="text-sm text-slate-500">
          Toggle the storefront between live, maintenance, and payments-only modes.
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Maintenance mode */}
        <div className="flex items-start justify-between gap-4 p-4 rounded-xl border border-slate-200">
          <div className="flex-1">
            <Label className="font-semibold text-slate-800">Maintenance mode</Label>
            <p className="text-sm text-slate-500 mt-1">
              When ON, all customers see a "We'll be back soon" page. The admin and staff dashboards stay accessible.
            </p>
            <div className="mt-3">
              <Label htmlFor="maintenance-msg" className="text-xs text-slate-500">Customer message</Label>
              <Input
                id="maintenance-msg"
                value={settings.maintenance_message || ''}
                onChange={(e) => setSettings({ ...settings, maintenance_message: e.target.value })}
                onBlur={() => update({ maintenance_message: settings.maintenance_message })}
                placeholder="We'll be back soon…"
                data-testid="maintenance-message-input"
              />
            </div>
          </div>
          <Switch
            checked={!!settings.maintenance_mode}
            disabled={saving}
            onCheckedChange={(v) => update({ maintenance_mode: v })}
            data-testid="maintenance-mode-toggle"
          />
        </div>

        {/* Online payments */}
        <div className="flex items-start justify-between gap-4 p-4 rounded-xl border border-slate-200">
          <div className="flex-1">
            <Label className="font-semibold text-slate-800 flex items-center gap-2">
              <CreditCard className="h-4 w-4 text-amber-600" /> Online payments (PayPal)
            </Label>
            <p className="text-sm text-slate-500 mt-1">
              When OFF, the PayPal checkout button is replaced with the message below. Cash payments and local pickup still work.
            </p>
            <div className="mt-3 space-y-3">
              <div>
                <Label htmlFor="payments-msg" className="text-xs text-slate-500">Disabled message</Label>
                <Input
                  id="payments-msg"
                  value={settings.payments_disabled_message || ''}
                  onChange={(e) => setSettings({ ...settings, payments_disabled_message: e.target.value })}
                  onBlur={() => update({ payments_disabled_message: settings.payments_disabled_message })}
                  data-testid="payments-disabled-message-input"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="contact-email" className="text-xs text-slate-500">Contact email</Label>
                  <Input
                    id="contact-email"
                    value={settings.contact_email || ''}
                    onChange={(e) => setSettings({ ...settings, contact_email: e.target.value })}
                    onBlur={() => update({ contact_email: settings.contact_email })}
                    placeholder="info@example.com"
                    data-testid="contact-email-input"
                  />
                </div>
                <div>
                  <Label htmlFor="contact-phone" className="text-xs text-slate-500">Contact phone (optional)</Label>
                  <Input
                    id="contact-phone"
                    value={settings.contact_phone || ''}
                    onChange={(e) => setSettings({ ...settings, contact_phone: e.target.value })}
                    onBlur={() => update({ contact_phone: settings.contact_phone })}
                    placeholder="555-555-5555"
                    data-testid="contact-phone-input"
                  />
                </div>
              </div>
            </div>
          </div>
          <Switch
            checked={settings.online_payments_enabled !== false}
            disabled={saving}
            onCheckedChange={(v) => update({ online_payments_enabled: v })}
            data-testid="online-payments-toggle"
          />
        </div>

        {settings.maintenance_mode && (
          <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
            <strong>Maintenance mode is currently ON.</strong> The storefront is showing the "We'll be back soon" page to customers. Visit any non-admin URL to verify.
          </div>
        )}
        {!settings.online_payments_enabled && (
          <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
            <strong>Online payments are OFF.</strong> PayPal checkout is hidden site-wide. Cash payment + local pickup still work.
          </div>
        )}

        {/* Cash App QR-based stopgap payment */}
        <div className="border-t border-slate-200 pt-4" data-testid="cashapp-settings">
          <div className="flex items-start justify-between gap-4 mb-3">
            <div className="flex items-start gap-3">
              <CreditCard className="h-5 w-5 text-green-600 mt-0.5" />
              <div>
                <Label className="text-base font-semibold">Cash App QR Payment</Label>
                <p className="text-xs text-slate-500 mt-1">
                  Show a Pay-with-Cash-App option at checkout. Customer scans the QR, sends manually. You confirm payment in the admin orders queue.
                </p>
              </div>
            </div>
            <Switch
              checked={!!settings.cashapp_enabled}
              disabled={saving}
              onCheckedChange={(v) => update({ cashapp_enabled: v })}
              data-testid="cashapp-toggle"
            />
          </div>
          {settings.cashapp_enabled && (
            <div className="ml-8 space-y-2">
              <div>
                <Label className="text-xs">Your Cash App handle</Label>
                <Input
                  value={settings.cashapp_handle || ''}
                  onChange={(e) => setSettings({ ...settings, cashapp_handle: e.target.value })}
                  onBlur={() => update({ cashapp_handle: settings.cashapp_handle })}
                  placeholder="$break4styles"
                  data-testid="cashapp-handle-input"
                />
              </div>
              <div>
                <Label className="text-xs">Note shown to customer</Label>
                <Input
                  value={settings.cashapp_note || ''}
                  onChange={(e) => setSettings({ ...settings, cashapp_note: e.target.value })}
                  onBlur={() => update({ cashapp_note: settings.cashapp_note })}
                  placeholder="For faster processing..."
                  data-testid="cashapp-note-input"
                />
              </div>
              <p className="text-xs text-slate-500">
                QR image is hosted at <code className="bg-slate-100 px-1 rounded">{settings.cashapp_qr_url}</code>. To swap it, upload a new file at that path on the server.
              </p>
            </div>
          )}
        </div>

        {/* Zelle QR-based stopgap payment — mirrors Cash App. */}
        <div className="border-t border-slate-200 pt-4" data-testid="zelle-settings">
          <div className="flex items-start justify-between gap-4 mb-3">
            <div className="flex items-start gap-3">
              <CreditCard className="h-5 w-5 text-purple-600 mt-0.5" />
              <div>
                <Label className="text-base font-semibold">Zelle QR Payment</Label>
                <p className="text-xs text-slate-500 mt-1">
                  Show a Pay-with-Zelle option at checkout. Customer scans the QR (or sends to your email), then you verify the deposit in your bank app.
                </p>
              </div>
            </div>
            <Switch
              checked={!!settings.zelle_enabled}
              disabled={saving}
              onCheckedChange={(v) => update({ zelle_enabled: v })}
              data-testid="zelle-toggle"
            />
          </div>
          {settings.zelle_enabled && (
            <div className="ml-8 space-y-2">
              <div>
                <Label className="text-xs">Zelle email or phone</Label>
                <Input
                  value={settings.zelle_handle || ''}
                  onChange={(e) => setSettings({ ...settings, zelle_handle: e.target.value })}
                  onBlur={() => update({ zelle_handle: settings.zelle_handle })}
                  placeholder="research@genorapeptides.com"
                  data-testid="zelle-handle-input"
                />
              </div>
              <div>
                <Label className="text-xs">Recipient name (shown to customer)</Label>
                <Input
                  value={settings.zelle_recipient_name || ''}
                  onChange={(e) => setSettings({ ...settings, zelle_recipient_name: e.target.value })}
                  onBlur={() => update({ zelle_recipient_name: settings.zelle_recipient_name })}
                  placeholder="GENORA LLC"
                  data-testid="zelle-recipient-input"
                />
              </div>
              <div>
                <Label className="text-xs">Note shown to customer</Label>
                <Input
                  value={settings.zelle_note || ''}
                  onChange={(e) => setSettings({ ...settings, zelle_note: e.target.value })}
                  onBlur={() => update({ zelle_note: settings.zelle_note })}
                  placeholder="For faster processing..."
                  data-testid="zelle-note-input"
                />
              </div>
              <p className="text-xs text-slate-500">
                QR image is hosted at <code className="bg-slate-100 px-1 rounded">{settings.zelle_qr_url}</code>. To swap it, upload a new file at that path on the server.
              </p>
            </div>
          )}
        </div>

        {/* Auto-cancel stale reservations — sweeps Cash App / Zelle / Reserve
            orders that customers never funded. Fires hourly. */}
        <div className="border-t border-slate-200 pt-4" data-testid="auto-cancel-settings">
          <div className="flex items-start justify-between gap-4 mb-3">
            <div className="flex items-start gap-3">
              <Clock className="h-5 w-5 text-amber-600 mt-0.5" />
              <div>
                <Label className="text-base font-semibold">Auto-cancel stale reservations</Label>
                <p className="text-xs text-slate-500 mt-1">
                  Automatically cancel Cash App / Zelle reservations that customers never fund. Same reversal as the red-X button (refund credit, release affiliate code, delete from ShipStation, email customer). Runs hourly.
                </p>
              </div>
            </div>
            <Switch
              checked={!!settings.auto_cancel_stale_reservations_enabled}
              disabled={saving}
              onCheckedChange={(v) => update({ auto_cancel_stale_reservations_enabled: v })}
              data-testid="auto-cancel-toggle"
            />
          </div>
          {settings.auto_cancel_stale_reservations_enabled && (
            <div className="ml-8">
              <Label className="text-xs">Cancel reservations older than (hours)</Label>
              <Input
                type="number"
                min="1"
                max="720"
                value={settings.auto_cancel_stale_reservations_hours ?? 48}
                onChange={(e) => setSettings({ ...settings, auto_cancel_stale_reservations_hours: parseInt(e.target.value, 10) || 48 })}
                onBlur={() => update({ auto_cancel_stale_reservations_hours: parseInt(settings.auto_cancel_stale_reservations_hours, 10) || 48 })}
                data-testid="auto-cancel-hours-input"
                className="max-w-[120px]"
              />
              <p className="text-xs text-slate-500 mt-1">
                Suggested: 24h (aggressive), 48h (recommended), 72h (relaxed). Max 720h (30 days).
              </p>
            </div>
          )}
        </div>

        <div className="border-t border-slate-200 pt-4" data-testid="payment-reminder-settings">
          <div className="flex items-start justify-between gap-4">
            <div>
              <Label className="text-sm font-medium">Payment reminder emails</Label>
              <p className="text-xs text-slate-500 mt-0.5">Nudge customers who reserved via Cash App / Zelle but haven't paid yet (two reminders before auto-cancel).</p>
            </div>
            <Switch
              checked={!!settings.payment_reminder_enabled}
              onCheckedChange={(v) => update({ payment_reminder_enabled: v })}
              data-testid="payment-reminder-toggle"
            />
          </div>
          {settings.payment_reminder_enabled && (
            <div className="ml-8 mt-3 flex gap-6">
              <div>
                <Label className="text-xs">First reminder after (hours)</Label>
                <Input
                  type="number" min="1" max="168"
                  value={settings.payment_reminder_hours_1 ?? 6}
                  onChange={(e) => setSettings({ ...settings, payment_reminder_hours_1: parseInt(e.target.value, 10) || 6 })}
                  onBlur={() => update({ payment_reminder_hours_1: parseInt(settings.payment_reminder_hours_1, 10) || 6 })}
                  data-testid="payment-reminder-hours-1-input"
                  className="max-w-[120px]"
                />
              </div>
              <div>
                <Label className="text-xs">Second reminder after (hours)</Label>
                <Input
                  type="number" min="1" max="168"
                  value={settings.payment_reminder_hours_2 ?? 24}
                  onChange={(e) => setSettings({ ...settings, payment_reminder_hours_2: parseInt(e.target.value, 10) || 24 })}
                  onBlur={() => update({ payment_reminder_hours_2: parseInt(settings.payment_reminder_hours_2, 10) || 24 })}
                  data-testid="payment-reminder-hours-2-input"
                  className="max-w-[120px]"
                />
              </div>
            </div>
          )}
        </div>

        <div className="border-t border-slate-200 pt-4" data-testid="cart-countdown-settings">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <Clock className="h-5 w-5 text-slate-600 mt-0.5" />
              <div>
                <Label className="text-base font-semibold">Cart hold-time countdown</Label>
                <p className="text-xs text-slate-500 mt-1">
                  Shows a 15-minute timer on each cart line and auto-removes items when it expires. Turn OFF for the reserve-then-pay (Zelle / Cash App) flow, where a countdown isn't needed. Off by default.
                </p>
              </div>
            </div>
            <Switch
              checked={!!settings.cart_countdown_enabled}
              disabled={saving}
              onCheckedChange={(v) => update({ cart_countdown_enabled: v })}
              data-testid="cart-countdown-toggle"
            />
          </div>
        </div>

        <div className="border-t border-slate-200 pt-4" data-testid="announcement-settings">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <Megaphone className="h-5 w-5 text-teal-600 mt-0.5" />
              <div>
                <Label className="text-base font-semibold">Site-wide announcement bar</Label>
                <p className="text-xs text-slate-500 mt-1">
                  Shows a banner at the top of every page — great for sales (e.g., 4th of July) and promo codes.
                </p>
              </div>
            </div>
            <Switch
              checked={!!settings.announcement_enabled}
              disabled={saving}
              onCheckedChange={(v) => update({ announcement_enabled: v })}
              data-testid="announcement-toggle"
            />
          </div>

          <div className="mt-4 space-y-3 pl-8">
            <div>
              <Label className="text-sm">Message</Label>
              <Input
                value={annMsg}
                onChange={(e) => setAnnMsg(e.target.value)}
                placeholder="🎆 4th of July Sale — 20% off all research peptides!"
                maxLength={140}
                data-testid="announcement-message-input"
              />
            </div>
            <div>
              <Label className="text-sm">Discount code (optional)</Label>
              <Input
                value={annCode}
                onChange={(e) => setAnnCode(e.target.value.toUpperCase())}
                placeholder="JULY4"
                maxLength={40}
                data-testid="announcement-code-input"
              />
              <p className="text-xs text-slate-400 mt-1">Shown as a copy-to-clipboard pill. Make sure a matching discount code exists in the Affiliates / Discount Codes section.</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-sm">Auto start (optional)</Label>
                <Input
                  type="datetime-local"
                  value={annStart}
                  onChange={(e) => setAnnStart(e.target.value)}
                  data-testid="announcement-start-input"
                />
              </div>
              <div>
                <Label className="text-sm">Auto end (optional)</Label>
                <Input
                  type="datetime-local"
                  value={annEnd}
                  onChange={(e) => setAnnEnd(e.target.value)}
                  data-testid="announcement-end-input"
                />
              </div>
            </div>
            <p className="text-xs text-slate-400 -mt-1">
              Leave blank for no limit. When set, the banner only shows within this window (uses the viewer's local time). The toggle above must stay ON.
            </p>
            <Button
              size="sm"
              disabled={saving}
              onClick={() => update({ announcement_message: annMsg, announcement_code: annCode, announcement_start: annStart, announcement_end: annEnd })}
              data-testid="announcement-save-btn"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
              Save announcement text
            </Button>
          </div>
        </div>

        <div className="border-t border-slate-200 pt-4" data-testid="free-shipping-settings">
          <div className="flex items-start gap-3">
            <Truck className="h-5 w-5 text-teal-600 mt-0.5" />
            <div className="flex-1">
              <Label className="text-base font-semibold">Free shipping threshold</Label>
              <p className="text-xs text-slate-500 mt-1 mb-3">
                Orders with a merchandise subtotal at or above this amount ship free (all payment methods). Set to 0 to charge shipping on every order.
              </p>
              <div className="flex items-center gap-2 max-w-xs">
                <span className="text-slate-500 text-sm">$</span>
                <Input
                  type="number"
                  min="0"
                  step="1"
                  value={freeShip}
                  onChange={(e) => setFreeShip(e.target.value)}
                  data-testid="free-shipping-threshold-input"
                />
                <Button
                  size="sm"
                  disabled={saving}
                  onClick={() => update({ free_shipping_threshold: parseFloat(freeShip) || 0 })}
                  data-testid="free-shipping-save-btn"
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                  Save
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-slate-200 pt-4" data-testid="shipping-cutoff-settings">
          <div className="flex items-start gap-3">
            <Clock className="h-5 w-5 text-amber-600 mt-0.5" />
            <div className="flex-1">
              <div className="flex items-center justify-between gap-3">
                <Label className="text-base font-semibold">Shipping cutoff notice</Label>
                <Switch
                  checked={cutoffEnabled}
                  onCheckedChange={(v) => { setCutoffEnabled(v); update({ shipping_cutoff_enabled: v }); }}
                  data-testid="shipping-cutoff-toggle"
                />
              </div>
              <p className="text-xs text-slate-500 mt-1 mb-3">
                Shows at checkout for shipped orders: "Orders placed after <strong>{cutoffTime || '3:00 PM PT'}</strong> will be sent the following day." Edit the time below to adjust seasonally.
              </p>
              <div className="flex items-center gap-2 max-w-xs">
                <Input
                  type="text"
                  value={cutoffTime}
                  onChange={(e) => setCutoffTime(e.target.value)}
                  placeholder="3:00 PM PT"
                  disabled={!cutoffEnabled}
                  data-testid="shipping-cutoff-time-input"
                />
                <Button
                  size="sm"
                  disabled={saving || !cutoffEnabled}
                  onClick={() => update({ shipping_cutoff_time: cutoffTime })}
                  data-testid="shipping-cutoff-save-btn"
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                  Save
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Customer re-engagement / lifecycle */}
        <div className="p-4 rounded-xl border border-slate-200">
          <div className="flex items-start gap-3">
            <UserX className="h-5 w-5 text-teal-600 mt-0.5" />
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <Label className="text-base font-semibold">Customer re-engagement</Label>
                <Switch
                  checked={reEnabled}
                  onCheckedChange={(v) => { setReEnabled(v); update({ reengagement_enabled: v }); }}
                  data-testid="reengagement-toggle"
                />
              </div>
              <p className="text-xs text-slate-500 mt-1 mb-3">
                Daily automation: warns never-purchased accounts they'll be deleted, then queues them for deletion after the grace window, and nudges lapsed members to restock. Accounts with at least one order become permanent members and are never deleted.
              </p>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label htmlFor="re-warn" className="text-xs text-slate-500">Warn after (days, no order)</Label>
                  <Input id="re-warn" type="number" min="1" value={reWarn} disabled={!reEnabled}
                    onChange={(e) => setReWarn(e.target.value)}
                    onBlur={() => update({ reengagement_warning_days: parseInt(reWarn) || 90 })}
                    data-testid="reengagement-warning-days" />
                </div>
                <div>
                  <Label htmlFor="re-grace" className="text-xs text-slate-500">Grace before delete (days)</Label>
                  <Input id="re-grace" type="number" min="0" value={reGrace} disabled={!reEnabled}
                    onChange={(e) => setReGrace(e.target.value)}
                    onBlur={() => update({ reengagement_grace_days: parseInt(reGrace) || 30 })}
                    data-testid="reengagement-grace-days" />
                </div>
                <div>
                  <Label htmlFor="re-restock" className="text-xs text-slate-500">Restock nudge after (days)</Label>
                  <Input id="re-restock" type="number" min="1" value={reRestock} disabled={!reEnabled}
                    onChange={(e) => setReRestock(e.target.value)}
                    onBlur={() => update({ reengagement_restock_days: parseInt(reRestock) || 60 })}
                    data-testid="reengagement-restock-days" />
                </div>
              </div>
              <p className="text-xs text-slate-400 mt-2">
                Manage who's due and run campaigns manually from the <strong>Re-engagement</strong> tab.
              </p>
              <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100">
                <div className="pr-3">
                  <Label className="text-sm font-medium text-slate-700">Weekly deletion digest</Label>
                  <p className="text-xs text-slate-500 mt-0.5">Email a weekly summary of the Pending Deletion queue to your contact email.</p>
                </div>
                <Switch
                  checked={reDigest}
                  disabled={!reEnabled}
                  onCheckedChange={(v) => { setReDigest(v); update({ reengagement_digest_enabled: v }); }}
                  data-testid="reengagement-digest-toggle"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Win-back incentive code (deletion-warning + abandoned-cart emails) */}
        <div className="p-4 rounded-xl border border-slate-200">
          <div className="flex items-start gap-3">
            <Megaphone className="h-5 w-5 text-teal-600 mt-0.5" />
            <div className="flex-1">
              <Label className="text-base font-semibold">Win-back incentive code</Label>
              <p className="text-xs text-slate-500 mt-1 mb-3">
                Shown in the account-deletion warning and abandoned-cart emails as a nudge to buy. Create the matching code on your live site (e.g. <strong>20PEPOFF</strong>). Leave blank to hide the discount block.
              </p>
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2">
                  <Label htmlFor="inc-code" className="text-xs text-slate-500">Discount code</Label>
                  <Input id="inc-code" placeholder="20PEPOFF" value={incentiveCode}
                    onChange={(e) => setIncentiveCode(e.target.value)}
                    onBlur={() => update({ incentive_discount_code: incentiveCode.trim() })}
                    data-testid="incentive-code-input" />
                </div>
                <div>
                  <Label htmlFor="inc-pct" className="text-xs text-slate-500">% off</Label>
                  <Input id="inc-pct" type="number" min="0" max="100" value={incentivePercent}
                    onChange={(e) => setIncentivePercent(e.target.value)}
                    onBlur={() => update({ incentive_discount_percent: parseInt(incentivePercent) || 0 })}
                    data-testid="incentive-percent-input" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Abandoned-cart reminder emails */}
        <div className="p-4 rounded-xl border border-slate-200">
          <div className="flex items-start gap-3">
            <ShoppingCart className="h-5 w-5 text-teal-600 mt-0.5" />
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <Label className="text-base font-semibold">Abandoned-cart reminders</Label>
                <Switch
                  checked={cartEnabled}
                  onCheckedChange={(v) => { setCartEnabled(v); update({ abandoned_cart_enabled: v }); }}
                  data-testid="abandoned-cart-toggle"
                />
              </div>
              <p className="text-xs text-slate-500 mt-1 mb-3">
                Emails signed-in shoppers who left items in their cart. First a "you left items" nudge, then a "still thinking about it?" reminder. Includes the win-back code above if set.
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="cart-first" className="text-xs text-slate-500">First email after (hours)</Label>
                  <Input id="cart-first" type="number" min="1" value={cartFirstHours} disabled={!cartEnabled}
                    onChange={(e) => setCartFirstHours(e.target.value)}
                    onBlur={() => update({ abandoned_cart_first_hours: parseInt(cartFirstHours) || 4 })}
                    data-testid="abandoned-cart-first-hours" />
                </div>
                <div>
                  <Label htmlFor="cart-reminder" className="text-xs text-slate-500">Reminder after (hours)</Label>
                  <Input id="cart-reminder" type="number" min="1" value={cartReminderHours} disabled={!cartEnabled}
                    onChange={(e) => setCartReminderHours(e.target.value)}
                    onBlur={() => update({ abandoned_cart_reminder_hours: parseInt(cartReminderHours) || 24 })}
                    data-testid="abandoned-cart-reminder-hours" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default SiteSettingsCard;
